import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { useDeclineReservationMutation } from '../../../../app/Slices/SevenDayApiSlice';
import { CustomButton } from '../../../../layout/styledcomponents/CustomButton';
import toast from '../../../shared/Toast';
import UserRole from '../../../../shared/AuthRoles';
import { theme } from '../../../../shared/theme';
import { trackEvent } from '../../../../app/Analytics';

export default function DeclineReservationModal(props: any) {
    const { onClose, open, onChange, message, id } = props;

    const loggedInUser = UserRole();
    const isMobile = useMediaQuery('(max-width: 770px)');

    const [declineReservation, { isLoading: isDeclining }] =
        useDeclineReservationMutation();

    const [declineReason, setDeclineReason] = useState('');
    const [declineComment, setDeclineComment] = useState('');

    const MAX_COMMENT_LENGTH = 160;
    const theme = useTheme();

    const reasons = useMemo(() => {
        const ownerReasons = [
            { value: 'Another Reservation', label: 'Another Reservation' },
            { value: 'Cleaning', label: 'Cleaning' },
            { value: 'Duplicate', label: 'Duplicate' },
            { value: 'Booking Cap Exceeded', label: 'Booking Cap Exceeded' },
            { value: 'Weather', label: 'Weather' },
            { value: 'Maintenance', label: 'Maintenance' },
            { value: 'Staff Unavailable', label: 'Staff Unavailable' },
            { value: 'Owners Request', label: 'Owners Request' },
            { value: 'Other', label: 'Other' },
        ];

        const guestReasons = [
            { value: 'Change of Plans', label: 'Change of plans' },
            { value: 'Found Alternative', label: 'Found an alternative' },
            { value: 'Too Expensive', label: 'Too expensive' },
            { value: 'Other', label: 'Other' },
        ];

        return loggedInUser?.role?.includes('owner')
            ? ownerReasons
            : guestReasons;
    }, [loggedInUser?.role]);

    const canSubmit =
        declineReason !== '' &&
        (declineReason !== 'Other' || declineComment.trim().length >= 3);

    const handleDialogClose = () => {
        if (isDeclining) return;
        onClose?.();
        trackEvent(
            'Reservations',
            'Decline Reservation',
            `Decline Reservation Modal Closed (${loggedInUser?.role})`,
        );
    };

    const confirmDecline = async () => {
        const comment =
            declineReason === 'Other'
                ? declineComment.trim()
                : declineComment.trim();

        const requestBody = [
            id,
            {
                reason: declineReason === 'Other' ? 'Other' : declineReason,
                comment,
            },
        ];

        try {
            await declineReservation(requestBody).unwrap();

            localStorage.setItem(
                'reservation-updated',
                JSON.stringify({ id, type: 'declined', ts: Date.now() }),
            );

            toast.success('Reservation has been declined');
            onChange?.();
            onClose?.();
            trackEvent(
                'Reservations',
                'Confirm Decline Reservation',
                `Confirm Decline Reservation Modal Closed (${loggedInUser?.role})`,
            );

            if (window.opener) window.close();
        } catch (e) {
            toast.error('Failed to decline reservation');
        }
    };

    return (
        <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth='sm'>
            <DialogTitle sx={{ alignItems: 'flex-start' }}>
                <Stack direction='row' spacing={1.5} alignItems='center'>
                    <WarningAmberRoundedIcon />
                    <Box>
                        Decline this reservation?
                        <Typography variant='body3' sx={{ opacity: 0.8 }}>
                            {message}
                        </Typography>
                    </Box>
                </Stack>

                <IconButton
                    onClick={handleDialogClose}
                    disabled={isDeclining}
                    size='small'
                    aria-label='close'
                    sx={{ color: 'text.primary' }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ py: 2.5 }}>
                <Stack spacing={2}>
                    <FormControl fullWidth size='small'>
                        <InputLabel
                            id='decline-reason-label'
                            sx={{
                                ...theme.typography.body1,
                                color: theme.palette.text.secondary,
                                fontSize: '12px',
                                lineHeight: 1.2,
                                transform: 'translate(14px, 14px) scale(1)',
                                '&.MuiInputLabel-shrink': {
                                    transform:
                                        'translate(14px, -9px) scale(0.75)',
                                },
                            }}
                        >
                            Select reason for decline
                        </InputLabel>
                        <Select
                            labelId='decline-reason-label'
                            value={declineReason}
                            label='Reason for decline'
                            onChange={(e) => {
                                const v = e.target.value;
                                setDeclineReason(v);
                                if (v !== 'Other') setDeclineComment('');
                                trackEvent(
                                    'Reservations',
                                    'Decline Reservation Reason',
                                    `Decline Reservation Reason (${v})`,
                                );
                            }}
                        >
                            {reasons.map((r) => (
                                <MenuItem key={r.value} value={r.value}>
                                    {r.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {declineReason === 'Other' ? (
                        <Stack spacing={0.75}>
                            <Stack
                                direction='row'
                                justifyContent='space-between'
                                alignItems='baseline'
                            >
                                <Typography
                                    variant='body3'
                                    sx={{ fontWeight: 700 }}
                                >
                                    Tell us briefly
                                </Typography>
                                <Typography
                                    variant='caption'
                                    sx={{
                                        opacity: 0.8,
                                        color:
                                            declineComment.length <=
                                            MAX_COMMENT_LENGTH
                                                ? 'text.secondary'
                                                : 'error.main',
                                    }}
                                >
                                    {MAX_COMMENT_LENGTH - declineComment.length}{' '}
                                    left
                                </Typography>
                            </Stack>

                            <TextField
                                fullWidth
                                value={declineComment}
                                onChange={(e) =>
                                    setDeclineComment(e.target.value)
                                }
                                label='Comment'
                                placeholder='e.g. Another booking already confirmed'
                                size={isMobile ? 'small' : 'medium'}
                                multiline
                                maxRows={3}
                                inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
                            />
                        </Stack>
                    ) : null}

                    <Typography variant='body3' sx={{ opacity: 0.75 }}>
                        Once declined, the guest will be notified.
                    </Typography>
                </Stack>
            </DialogContent>

            <Divider />

            <DialogActions
                sx={{
                    px: 2.5,
                    py: 2,
                    justifyContent: 'space-between',
                }}
            >
                <Grid container spacing={1} alignItems='center'>
                    <Grid item xs={12} sm='auto'>
                        <CustomButton
                            variant='contained'
                            color='neutral'
                            onClick={handleDialogClose}
                            disabled={isDeclining}
                            fullWidth={isMobile}
                        >
                            Keep reservation
                        </CustomButton>
                    </Grid>

                    <Grid item xs={12} sm='auto' sx={{ ml: 'auto' }}>
                        <CustomButton
                            loading={isDeclining}
                            variant='contained'
                            onClick={confirmDecline}
                            disabled={!canSubmit}
                            fullWidth={isMobile}
                            color='warning'
                            sx={{
                                borderRadius: 2,
                                fontWeight: 800,
                                textTransform: 'none',
                            }}
                        >
                            Decline reservation
                        </CustomButton>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    );
}
